import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectActiveMediaDevice } from 'features/user-media/userMediaSlice';
import { AudioPool, UnlockedAudio } from 'utils/audio/AudioPool';

export const useAudio = () => {
  const [attached, setAttached] = useState(false);
  const [failedOutputId, setFailedOutputId] = useState<string | null>(null);
  const audioOutputId = useAppSelector((state) => selectActiveMediaDevice(state, 'audiooutput'));
  const audio = useRef<HTMLAudioElement | UnlockedAudio | null>(null);
  const sinkIdFailed = !!audioOutputId && failedOutputId === audioOutputId;

  const clearSinkFailure = useCallback((routedId: string) => {
    setFailedOutputId((failed) => (failed === routedId ? null : failed));
  }, []);

  const resetAudio = useCallback(() => {
    if (!audio.current) {
      return;
    }

    audio.current.pause();
    audio.current.currentTime = 0;
    audio.current.onplay = null;
    audio.current.onended = null;
    audio.current.onerror = null;
    audio.current.removeAttribute('src');
    audio.current.load();
  }, []);

  useEffect(() => {
    const init = async () => {
      audio.current = await AudioPool.obtainElement();
      if (audioOutputId && audio.current?.setSinkId) {
        try {
          await audio.current.setSinkId(audioOutputId);
          clearSinkFailure(audioOutputId);
        } catch {
          // Safari refuses setSinkId outside a user gesture; applySinkId retries it from the click.
        }
      }
      setAttached(true);
    };

    init();

    return () => {
      if (audio.current) {
        audio.current.pause();
        if (audio.current instanceof UnlockedAudio && !audio.current.unloaded) {
          AudioPool.unloadElement(audio.current);
        }
      }
    };
  }, [audioOutputId, clearSinkFailure]);

  useEffect(() => {
    if (!audio.current) {
      return;
    }

    if (attached && audioOutputId && audio.current.setSinkId) {
      audio.current
        .setSinkId(audioOutputId)
        .then(() => clearSinkFailure(audioOutputId))
        .catch(() => {
          // Safari refuses setSinkId outside a user gesture; applySinkId retries it from the click.
        });
    }
  }, [audioOutputId, attached, clearSinkFailure]);

  // Call synchronously from a click: Safari only allows setSinkId during user activation.
  const applySinkId = useCallback(async () => {
    const element = audio.current;
    if (!audioOutputId || !element?.setSinkId || element.sinkId === audioOutputId) {
      return;
    }
    try {
      await element.setSinkId(audioOutputId);
      clearSinkFailure(audioOutputId);
    } catch {
      setFailedOutputId(audioOutputId);
    }
  }, [audioOutputId, clearSinkFailure]);

  return {
    attached,
    audio: audio.current,
    resetAudio,
    sinkIdFailed,
    applySinkId,
  };
};
